import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { FlexColumn } from '@/atoms/FlexContainers'
import { AsH2, Text } from '@/atoms/Text'
import { GET_THEATRICAL_USER_PREFERENCES } from '@/organisms/TheaterPreferences/queries'
import { useUser } from '@/services/UserService'
import { ReservationObject, TheatricalReleaseObject, TheatricalShowtimeVenue } from '@/types/codegen-federation'
import { useSafeAnalytics } from '@/utils/analytics'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { EnhancedShowtimeProvider } from '@/views/TicketCheckoutViews/ShowtimesView/components/EnhancedShowtimeContext'
import ExclusiveShowtimeDateWarning from '@/views/TicketCheckoutViews/ShowtimesView/components/ExclusiveShowtimeDateWarning'
import { SoldOutInfoIconTooltip } from '@/views/TicketCheckoutViews/ShowtimesView/components/SoldOutInfoIconTooltip'
import VenueList, { LocaleWarningModalState } from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueList'
import VenueListSkeleton from '@/views/TicketCheckoutViews/ShowtimesView/components/VenueListSkeleton'
import BuyoutModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/BuyoutModal/BuyoutModal'
import { GroupTicketsModal } from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/GroupTicketsModal'
import LocalWarningModal from '@/views/TicketCheckoutViews/ShowtimesView/components/modals/LocalWarningModal'

interface Props {
  venues: TheatricalShowtimeVenue[]
  theatricalRelease: TheatricalReleaseObject
  isLoading: boolean
  reservation: ReservationObject | undefined
  isExclusiveDateSelected?: boolean
}
const Venues: FC<Props> = ({ venues, theatricalRelease, isLoading, reservation, isExclusiveDateSelected }) => {
  const { t } = useTranslate('tickets')
  const { isReady, query } = useSafeRouter()
  const [buyoutVenue, setBuyoutVenue] = useState<TheatricalShowtimeVenue | null>(null)
  const [buyoutVenueChain, setBuyoutVenueChain] = useState('')
  const [isGroupTicketsModalOpen, setIsGroupTicketsModalOpen] = useState(false)
  const [isBuyoutModalOpen, setIsBuyoutModalOpen] = useState(false)
  const soldOut = theatricalRelease?.soldOutShowtimeCount ?? 0
  const { track } = useSafeAnalytics()
  const reservationId = reservation?.id
  const { isLoggedIn } = useUser()

  const theaterSlug = theatricalRelease.theatricalSlug

  const queryModal = getItemOrFirstEntry(query?.modal)

  const { data: preferencesData } = useQuery(GET_THEATRICAL_USER_PREFERENCES, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: !isLoggedIn,
  })

  const preferredVenueID = preferencesData?.theatricalUserPreferences?.preferredVenues?.[0]?.providerId

  const venuesWithShowtimes = useMemo(() => {
    const filtered = venues?.filter((venue) => venue?.showtimes?.length > 0)
    if (!preferredVenueID) return filtered

    return filtered.sort((a, b) => {
      if (a.providerId === preferredVenueID) return -1
      if (b.providerId === preferredVenueID) return 1
      return 0
    })
  }, [venues, preferredVenueID])

  const venuesWithNoShowtimes = useMemo(() => {
    const filtered = venues?.filter((venue) => venue?.showtimes?.length === 0)
    if (!preferredVenueID) return filtered

    return filtered.sort((a, b) => {
      if (a.providerId === preferredVenueID) return -1
      if (b.providerId === preferredVenueID) return 1
      return 0
    })
  }, [venues, preferredVenueID])

  const openGroupTicketsModal = useCallback(() => {
    setIsGroupTicketsModalOpen(true)
  }, [])

  const openBuyoutModal = useCallback((buyoutVenue: TheatricalShowtimeVenue, theaterChain: string) => {
    setIsBuyoutModalOpen(true)
    setBuyoutVenue(buyoutVenue)
    setBuyoutVenueChain(theaterChain)
  }, [])

  useEffect(() => {
    if (query?.highlightGroupTickets || query?.groupDiscountCode) {
      openGroupTicketsModal()
    }
  }, [openGroupTicketsModal, query?.highlightGroupTickets, query?.groupDiscountCode])

  const [localeWarningModalState, setLocaleWarningModalState] = useState<LocaleWarningModalState>({
    isOpen: false,
    warning: '',
    callback: () => null,
  })

  const closeLocaleWarningModal = useCallback(() => {
    track('Canceled Showtime From Language Modal')
    setLocaleWarningModalState({ isOpen: false, warning: '', callback: () => null })
  }, [track])

  if (isLoading || !isReady) return <VenueListSkeleton />

  return (
    <EnhancedShowtimeProvider venues={venues}>
      <FlexColumn className="min-h-[50vh] gap-6 md:min-h-[55vh]">
        <FlexColumn className="gap-4">
          <FlexColumn className="gap-6">
            {isExclusiveDateSelected && <ExclusiveShowtimeDateWarning theatricalSlug={theaterSlug} />}
            {theatricalRelease?.soldOutShowtimeCount &&
              theatricalRelease?.soldOutShowtimeCount > 9 &&
              venuesWithShowtimes?.length > 0 && (
                <div
                  className="inline-block rounded-lg border-l-4 !border-error-300 bg-error-100 px-2 py-3"
                  role="alert"
                >
                  <Text as={AsH2} className="photon-caption-md md:photon-caption-lg">
                    <Translate i18nKey="soldOutShowtimesNationwide" t={t} values={{ soldOut }}>
                      <span className="photon-caption-xl font-semibold">{{ soldOut }}</span> sold out theaters
                      nationwide. Selling Fast! <SoldOutInfoIconTooltip />
                    </Translate>
                  </Text>
                </div>
              )}
          </FlexColumn>
          <VenueList
            venues={venuesWithShowtimes}
            theatricalRelease={theatricalRelease}
            theaterSlug={theaterSlug}
            setLocaleWarningModalState={setLocaleWarningModalState}
            isGroupTicketsModalOpen={isGroupTicketsModalOpen}
            openGroupTicketsModal={openGroupTicketsModal}
            reservationId={reservationId}
            openBuyoutModal={openBuyoutModal}
            isBuyoutModalOpen={isBuyoutModalOpen}
            theatricalSlug={theaterSlug}
            showGuildTicketBanner
            showGiveawayEmptyState
          />
        </FlexColumn>

        {venuesWithNoShowtimes?.length > 0 && !isExclusiveDateSelected && (
          <FlexColumn className="gap-4">
            <Text as={AsH2} className="photon-title-md md:photon-title-lg" weight="bold">
              {t('noAvailableShowtimes', 'No Available Showtimes')}
            </Text>
            <VenueList
              venues={venuesWithNoShowtimes}
              theatricalRelease={theatricalRelease}
              theaterSlug={theaterSlug}
              setLocaleWarningModalState={setLocaleWarningModalState}
              isGroupTicketsModalOpen={isGroupTicketsModalOpen}
              openGroupTicketsModal={openGroupTicketsModal}
              reservationId={reservationId}
              openBuyoutModal={openBuyoutModal}
              isBuyoutModalOpen={isBuyoutModalOpen}
              theatricalSlug={theaterSlug}
              showGuildTicketBanner={Boolean(!venuesWithShowtimes?.length)}
              showGiveawayEmptyState={Boolean(!venuesWithShowtimes?.length)}
            />
          </FlexColumn>
        )}
      </FlexColumn>
      <LocalWarningModal onClose={closeLocaleWarningModal} localeWarningModalState={localeWarningModalState} />
      {isGroupTicketsModalOpen && theatricalRelease?.groupProductVariantId && (
        <GroupTicketsModal
          theatricalSlug={theaterSlug}
          open={isGroupTicketsModalOpen}
          onClose={() => setIsGroupTicketsModalOpen(false)}
          groupTicketProductVariantId={theatricalRelease.groupProductVariantId}
          queryModal={queryModal}
        />
      )}
      <BuyoutModal
        open={isBuyoutModalOpen}
        setOpen={setIsBuyoutModalOpen}
        buyoutVenue={buyoutVenue}
        venueChain={buyoutVenueChain}
        openGroupTicket={openGroupTicketsModal}
      />
    </EnhancedShowtimeProvider>
  )
}

export default Venues
